
import { NavLink } from "./NavLink";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Calendar, LayoutDashboard, FolderHeart, Briefcase, CalendarCheck } from "lucide-react";

interface MainNavProps {
  isHost: boolean;
  isAuthenticated: boolean;
}

export const MainNav = ({ isHost, isAuthenticated }: MainNavProps) => {
  // Host status debugging
  console.log('MainNav rendering with isHost:', isHost);

  // First get the current user's ID
  const { data: userId } = useQuery({
    queryKey: ['current-user'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id;
    },
  });

  // Then query admin_users with maybeSingle() to handle no results gracefully
  const { data: adminUser } = useQuery({
    queryKey: ['admin-user', userId],
    enabled: !!userId,
    queryFn: async () => {
      console.log('Checking admin status for user:', userId);
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching admin user:', error);
        return null;
      }
      console.log('Admin user data:', data);
      return data;
    },
  });

  // Additional debugging for host status
  console.log('MainNav isHost value:', isHost);

  return (
    <nav className="flex items-center space-x-4">
      <NavLink to="/search" className="text-sm">Explore</NavLink>
      <NavLink to="/recommendations" className="text-sm">Recommendations</NavLink>
      <NavLink to="/visa-requirements" className="text-sm">Visa Requirements</NavLink>
      <NavLink to="/collections" className="flex items-center gap-1 text-sm">
        <FolderHeart className="h-3.5 w-3.5" />
        <span>Collections</span>
      </NavLink>
      {isAuthenticated && (
        <NavLink to="/trips" className="flex items-center gap-1 text-sm">
          <Briefcase className="h-3.5 w-3.5" />
          <span>My Trips</span>
        </NavLink>
      )}
      {isHost && (
        <>
          <NavLink to="/availability" className="flex items-center gap-1 text-sm">
            <Calendar className="h-3.5 w-3.5" />
            <span>Manage Availability</span>
          </NavLink>
          <NavLink to="/trips?tab=bookings" className="flex items-center gap-1 text-sm">
            <CalendarCheck className="h-3.5 w-3.5" />
            <span>My Bookings</span>
          </NavLink>
        </>
      )}
      {adminUser && (
        <NavLink to="/admin" className="flex items-center gap-1 text-sm">
          <LayoutDashboard className="h-3.5 w-3.5" />
          <span>Admin Dashboard</span>
        </NavLink>
      )}
    </nav>
  );
};
